import React, { useContext, useEffect, useRef, useState } from 'react'
import queryString from 'query-string'
import log from 'loglevel'
import { useLocation } from 'react-router-dom'
import {
  ControlMapping,
  RECEPTOR_TYPE,
  ReceptorRecord,
} from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { Receptor, getReceptorTemplate } from 'src/xgenerated'
import { SectionDef } from 'src/xgenerated/receptor'
import { useCurrentReceptor } from 'src/context/FormContext/CurrentReceptorContext'
import { useControlMappingsForReceptor, useReceptor } from '../receptors.hooks'
import { ControlMappingEntry } from '../modals/ConfigureReceptorForm/ConfigureReceptorForm.constants'
import { ActivateReceptorQueryParams } from '../modals/ConfigureReceptorForm/ConfigureReceptorForm'
import { createNewControlMapping } from '../modals/ConfigureReceptorForm/ConfigureReceptorForm.helpers'

export type ReceptorsConfigType = Record<
  string,
  Record<string, Record<string, string>[]>
>

type ReceptorsConfigContextType = {
  receptor: ReceptorRecord.AsObject | null
  mappings: Record<string, ControlMappingEntry[]>
  config: ReceptorsConfigType
  description: string
  setDescription: React.Dispatch<React.SetStateAction<string>>
  setConfig: React.Dispatch<React.SetStateAction<ReceptorsConfigType>>
  addMappingFunc: (fieldName: string, fieldKey: string) => void
  removeMappingFunc: (
    fieldName: string,
    fieldKey: string,
    index: number,
    isManualConfig: boolean,
  ) => void
  errorMsg: string
  setMappings: React.Dispatch<
    React.SetStateAction<Record<string, ControlMappingEntry[]>>
  >
  updateMappings: (
    fieldName: string,
    index: number,
    newMapping: ControlMappingEntry,
  ) => void
  validateMappings: () => boolean
  initialMappings: React.MutableRefObject<Record<string, ControlMappingEntry[]>>
}

const ReceptorConfigContext = React.createContext<ReceptorsConfigContextType>({
  receptor: null,
  mappings: {},
  config: {} as ReceptorsConfigType,
  description: '',
  setDescription: () => null,
  setConfig: () => null,
  addMappingFunc: () => null,
  removeMappingFunc: (
    _fieldName: string,
    _fieldKey: string,
    _index: number,
    _isManualConfig: boolean,
  ) => null,
  errorMsg: '',
  setMappings: () => null,
  updateMappings: (
    _fieldName: string,
    _index: number,
    _newMapping: ControlMappingEntry,
  ) => null,
  validateMappings: () => true,
  initialMappings: {
    current: {},
  },
})

export const useReceptorConfigContext = (): ReceptorsConfigContextType =>
  useContext(ReceptorConfigContext)

const convertDBMappings = (
  mappings: ControlMapping[],
): Record<string, ControlMappingEntry[]> => {
  const convertedMappings: Record<string, ControlMappingEntry[]> = {}

  mappings.forEach((mapping) => {
    const fieldKey = mapping.getType()

    if (!convertedMappings[fieldKey]) {
      convertedMappings[fieldKey] = []
    }
    const control = mapping.getControl()
    const source = mapping.getSource()
    if (!control || !source) {
      return
    }

    if (fieldKey === 'stacked_mappings') {
      const sourceValue = source.getValue() || ''
      const isSplit = sourceValue.split(':')

      convertedMappings[fieldKey].push({
        control: {
          name: control.getName() || '',
          value: control.getValue() || '',
        },
        source: {
          name: source.getName() || '',
          value: sourceValue,
        },
        isSplit: isSplit[2] == '1' ? true : false,
      })
    } else {
      convertedMappings[fieldKey].push({
        control: {
          name: control.getName() || '',
          value: control.getValue() || '',
        },
        source: {
          name: source.getName() || '',
          value: source.getValue() || '',
        },
      })
    }
  })
  return convertedMappings
}

const initMappings = (
  sections: SectionDef[],
): Record<string, ControlMappingEntry[]> => {
  const initialMappings: Record<string, ControlMappingEntry[]> = {}
  sections.forEach((section) => {
    section.fields.forEach((field) => {
      initialMappings[field.field] = [
        createNewControlMapping(field.display, field.field),
      ]
    })
  })
  return initialMappings
}

const deflateProjectMappings = (
  entries: ControlMappingEntry[],
): Record<string, string>[] => {
  const seen = new Set<string>()
  return entries.reduce<Record<string, string>[]>((acc, m) => {
    const pid = String(m.control.value)
    const tid = String(m.source.value)
    const key = `${pid}|${tid}|${m.name}|${m.source.parentId || ''}`
    if (seen.has(key)) return acc
    seen.add(key)
    acc.push({
      name: m.name ?? '',
      pid,
      pname: m.control.name as string,
      tid,
      tname: m.source.name as string,
      tpid: m.source.parentId ?? '',
    })
    return acc
  }, [])
}

// Advanced Mappings: serialize subject/enum selections alongside control/source
const deflateAdvanceMappings = (
  entries: ControlMappingEntry[],
): Record<string, string>[] => {
  const seen = new Set<string>()
  return entries.reduce<Record<string, string>[]>((acc, m) => {
    const pid = String(m.control.value)
    const tid = String(m.source.value)
    const sfieldId = m.subjectField?.value ? String(m.subjectField.value) : ''
    const efieldId = m.enumField?.value ? String(m.enumField.value) : ''
    const echoiceId = m.enumChoice?.value ? String(m.enumChoice.value) : ''
    const key = `${pid}|${tid}|${sfieldId}|${efieldId}|${echoiceId}|${m.name}|${
      m.source.parentId || ''
    }`
    if (seen.has(key)) return acc
    seen.add(key)
    acc.push({
      name: m.name ?? '',
      pid,
      pname: m.control.name as string,
      tid,
      tname: m.source.name as string,
      tpid: m.source.parentId ?? '',
      sfield_id: sfieldId,
      sfield_name: (m.subjectField?.name as string) ?? '',
      efield_id: efieldId,
      efield_name: (m.enumField?.name as string) ?? '',
      echoice_id: echoiceId,
      echoice_name: (m.enumChoice?.name as string) ?? '',
    })
    return acc
  }, [])
}

const inflateProjectMappingsToEntries = (
  items: Record<string, string>[],
): ControlMappingEntry[] => {
  return items.map((it) => ({
    name: it.name,
    control: { name: it.pname, value: it.pid },
    source: { name: it.tname, value: it.tid, parentId: it.tpid },
    nameError: '',
    controlError: '',
    sourceError: '',
    // Advanced fields default to empty for project mappings
    subjectFieldError: '',
    enumFieldError: '',
    enumChoiceError: '',
  }))
}

const inflateAdvanceMappingsToEntries = (
  items: Record<string, string>[],
): ControlMappingEntry[] => {
  return items.map((it) => ({
    name: it.name,
    control: { name: it.pname, value: it.pid },
    source: { name: it.tname, value: it.tid, parentId: it.tpid },
    subjectField: { name: it.sfield_name, value: it.sfield_id },
    enumField: { name: it.efield_name, value: it.efield_id },
    enumChoice: { name: it.echoice_name, value: it.echoice_id },
    nameError: '',
    controlError: '',
    sourceError: '',
    subjectFieldError: '',
    enumFieldError: '',
    enumChoiceError: '',
  }))
}

const inflateMappingsFromConfig = (
  cfg: ReceptorsConfigType,
): Record<string, ControlMappingEntry[]> => {
  const result: Record<string, ControlMappingEntry[]> = {}

  const rawPM = (cfg['fetch_sources']?.['project_mappings'] ?? []) as Record<
    string,
    string
  >[]
  if (rawPM.length) {
    result['project_mappings'] = inflateProjectMappingsToEntries(rawPM)
  }

  const rawAM = (cfg['fetch_sources']?.['advance_mappings'] ?? []) as Record<
    string,
    string
  >[]
  if (rawAM.length) {
    result['advance_mappings'] = inflateAdvanceMappingsToEntries(rawAM)
  }

  return result
}

export const ReceptorConfigContextProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const location = useLocation()
  const params = queryString.parse(location.search)
  const receptorId = params[ActivateReceptorQueryParams.RECEPTOR_ID] as string
  const { data: receptorFromParams } = useReceptor({
    receptorId,
    shouldFetch: true,
  })
  const [receptor, setReceptor] = useState<ReceptorRecord.AsObject | null>(null)
  const initialMappings = useRef<Record<string, ControlMappingEntry[]>>({})
  const {
    data: controlMappingsData,
    isLoading,
    error,
  } = useControlMappingsForReceptor(receptor?.id || '')
  const { receptor: receptorFromContext, setReceptor: setCurrentReceptor } =
    useCurrentReceptor()
  const [config, setConfig] = useState<ReceptorsConfigType>(
    {} as ReceptorsConfigType,
  )
  const [description, setDescription] = useState<string>('')
  const [mappings, setMappings] = useState<
    Record<string, ControlMappingEntry[]>
  >({})
  const [errorMsg, _setErrorMsg] = useState<string>('')
  const [receptorTemplate, setReceptorTemplate] = useState<Receptor | null>(
    null,
  )

  const parseConfigOrEmpty = (raw?: string): ReceptorsConfigType => {
    if (!raw || raw.trim() === '') return {} as ReceptorsConfigType
    try {
      const parsed = JSON.parse(raw)

      if (parsed && typeof parsed === 'object') {
        return parsed as ReceptorsConfigType
      } else {
        return {} as ReceptorsConfigType
      }
    } catch {
      return {} as ReceptorsConfigType
    }
  }

  useEffect(() => {
    let currentReceptor: ReceptorRecord.AsObject | undefined
    if (receptorFromContext.id.length > 0) {
      currentReceptor = receptorFromContext
    } else if (receptorFromParams) {
      currentReceptor = receptorFromParams.toObject()
    }
    if (!currentReceptor || isLoading || error) {
      return
    }

    const template = getReceptorTemplate(currentReceptor.modelid)
    setCurrentReceptor(currentReceptor)
    setReceptor(currentReceptor)
    setConfig(parseConfigOrEmpty(currentReceptor?.config))
    setDescription(currentReceptor?.description || '')
    setReceptorTemplate(template)
  }, [
    receptorFromContext,
    receptorFromParams,
    setCurrentReceptor,
    isLoading,
    error,
  ])

  useEffect(() => {
    if (!receptorTemplate || !controlMappingsData) {
      return
    }

    const controlMappings = controlMappingsData.getControlMappingsList() || []
    const dbMappings = convertDBMappings(controlMappings)
    // initialize the array regardless
    const initialMappingValues = initMappings(
      receptorTemplate.config_template || [],
    )
    Object.keys(initialMappingValues).forEach((key) => {
      if (dbMappings[key]) {
        initialMappingValues[key] = dbMappings[key]
      }
    })

    setMappings(initialMappingValues)
    initialMappings.current = initialMappingValues
  }, [receptorTemplate, controlMappingsData])

  useEffect(() => {
    if (!receptorTemplate) {
      return
    }

    if (receptorTemplate.type !== RECEPTOR_TYPE.WORKFLOW_PLATFORM) {
      return
    }

    const initialMappingValues = initMappings(
      receptorTemplate.config_template || [],
    )

    // Overlay mappings derived from config (inflated)
    const cfgMappings = inflateMappingsFromConfig(config)
    Object.keys(cfgMappings).forEach((key) => {
      if (cfgMappings[key]) {
        initialMappingValues[key] = cfgMappings[key]
      }
    })

    setMappings(initialMappingValues)
    initialMappings.current = initialMappingValues
  }, [receptorTemplate, config])

  if (error) {
    log.error('Error fetching control mappings', error)
  }

  const addMappingFunc = (fieldName: string, fieldKey: string) => {
    const mapping = createNewControlMapping(fieldName, fieldKey)
    setMappings({
      ...mappings,
      [fieldKey]: [...(mappings[fieldKey] || []), mapping],
    })
  }

  const removeMappingFunc = (
    fieldName: string,
    fieldKey: string,
    index: number,
    _isManualConfig: boolean,
  ) => {
    const current = mappings[fieldKey] || []
    const copy = [...current]
    copy.splice(index, 1)

    if (
      fieldKey === 'project_mappings' ||
      fieldKey === 'advance_mappings' ||
      fieldKey === 'stacked_mappings'
    ) {
      if (copy.length === 0) {
        copy.push(createNewControlMapping(fieldName, fieldKey))
      }
      setMappings({ ...mappings, [fieldKey]: copy })
      updateConfig(fieldKey, copy)
    } else {
      if (copy.length === 0) {
        copy.push(createNewControlMapping(fieldName, fieldKey))
      }
      setMappings({ ...mappings, [fieldKey]: copy })
    }
  }

  const updateMappings = (
    fieldKey: string,
    index: number,
    newMapping: ControlMappingEntry,
  ) => {
    const current = mappings[fieldKey] || []
    const newFieldMappings = [...current]
    newFieldMappings[index] = newMapping

    const newMappings = { ...mappings, [fieldKey]: newFieldMappings }
    setMappings(newMappings)
    updateConfig(fieldKey, newFieldMappings)
  }

  const updateConfig = (
    fieldKey: string,
    mappingsForKey: ControlMappingEntry[],
  ) => {
    if (fieldKey === 'project_mappings') {
      const flat = deflateProjectMappings(mappingsForKey)
      const newConfig = { ...config }
      if (!newConfig['fetch_sources']) {
        newConfig['fetch_sources'] = {}
      }
      newConfig['fetch_sources'][fieldKey] = flat
      setConfig(newConfig)
    } else if (fieldKey === 'advance_mappings') {
      const flat = deflateAdvanceMappings(mappingsForKey)
      const newConfig = { ...config }
      if (!newConfig['fetch_sources']) {
        newConfig['fetch_sources'] = {}
      }
      newConfig['fetch_sources'][fieldKey] = flat
      setConfig(newConfig)
    } else if (fieldKey === 'stacked_mappings') {
      const fetchSourceIds = new Set<string>()
      const fetchSources: { display: string; id: string }[] = []
      mappingsForKey.forEach((mapping) => {
        const srcVal = mapping.source?.value as string
        const srcStr = srcVal.split(':')
        const idStr = srcStr[0]
        const siteId = srcStr[1]
        const strSplit = mapping.isSplit ? '1' : '0'

        if (fetchSourceIds.has(srcVal)) return
        fetchSources.push({
          display: (mapping.source?.name as string) || '',
          id: idStr + ':' + siteId + ':' + strSplit,
        })
        fetchSourceIds.add(srcVal)
      })
      const newConfig = { ...config }
      if (!newConfig['fetch_sources']) {
        newConfig['fetch_sources'] = {}
      }
      newConfig['fetch_sources'][fieldKey] = fetchSources
      setConfig(newConfig)
    } else {
      const fetchSourceIds = new Set<string>()
      const fetchSources: { display: string; id: string }[] = []
      mappingsForKey.forEach((mapping) => {
        if (!mapping.source?.value) {
          return
        }
        const id = mapping.source.value.toString()
        if (fetchSourceIds.has(id)) {
          return
        }
        fetchSources.push({
          display: mapping.source.name as string,
          id,
        })
        fetchSourceIds.add(id)
      })
      const newConfig = { ...config }
      if (!newConfig['fetch_sources']) {
        newConfig['fetch_sources'] = {}
      }
      newConfig['fetch_sources'][fieldKey] = fetchSources
      setConfig(newConfig)
    }
  }

  const validateMappings = (): boolean => {
    let valid = true
    let hasMappings = false
    const copy = { ...mappings }

    Object.keys(copy).forEach((key) => {
      const seenMappings: Record<string, Set<string>> = {}
      const seenNameMapping: Set<string> = new Set()
      // For advance_mappings we only need to ensure no duplicate SOURCE across rows
      const seenAdvanceSources: Set<string> = new Set()

      copy[key].forEach((mapping, idx) => {
        // Project Mappings has 3 input checking compared to the traditional control and source
        if (key === 'project_mappings') {
          const missingControl = !mapping.control?.value
          const missingSource = !mapping.source?.value
          const missingName = !mapping.name

          const isRowCompletelyEmpty =
            !mapping.control?.value && !mapping.source?.value && !mapping.name

          // Optional behavior: allow empty placeholder rows without triggering errors
          if (isRowCompletelyEmpty) {
            return
          }

          if (missingControl || missingSource || missingName) {
            if (missingControl) {
              copy[key][idx].controlError = 'Please select a value'
            }
            if (missingSource) {
              copy[key][idx].sourceError = 'Please select a value'
            }
            if (missingName) {
              copy[key][idx].nameError = 'Please enter a name'
            }
            valid = false
            return
          }

          const nameKey = (mapping.name ?? '').trim()
          if (nameKey && seenNameMapping.has(nameKey)) {
            copy[key][idx].nameError = 'Duplicate name'
            valid = false
            return
          }
          if (nameKey) seenNameMapping.add(nameKey)

          // Duplicate pair check (control+source) for project_mappings
          const controlId = String(mapping.control?.value)
          const sourceId = String(mapping.source?.value)
          if (!seenMappings[sourceId]) {
            seenMappings[sourceId] = new Set()
          }
          if (seenMappings[sourceId].has(controlId)) {
            copy[key][idx].controlError = 'Duplicate entry'
            valid = false
            return
          }
          seenMappings[sourceId].add(controlId)

          hasMappings = true
        } else if (key === 'advance_mappings') {
          const missingSource = !mapping.source?.value
          const missingSubject = !mapping.subjectField?.value
          const missingEnumField = !mapping.enumField?.value
          const missingEnumChoice = !mapping.enumChoice?.value

          // Treat Advanced tab as OPTIONAL: skip validation for completely empty rows
          const isRowCompletelyEmpty =
            !mapping.source?.value &&
            !mapping.subjectField?.value &&
            !mapping.enumField?.value &&
            !mapping.enumChoice?.value

          if (isRowCompletelyEmpty) {
            return
          }

          // If the user started filling anything, enforce full validation
          if (
            missingSource ||
            missingSubject ||
            missingEnumField ||
            missingEnumChoice
          ) {
            if (missingSource)
              copy[key][idx].sourceError = 'Please select a value'
            if (missingSubject)
              copy[key][idx].subjectFieldError = 'Please select a subject field'
            if (missingEnumField)
              copy[key][idx].enumFieldError = 'Please select an enum field'
            if (missingEnumChoice)
              copy[key][idx].enumChoiceError = 'Please select an enum choice'

            valid = false
            return
          }

          // Enforce UNIQUE SOURCE across all advanced rows (regardless of other fields)
          const sourceId = String(mapping.source?.value)
          if (seenAdvanceSources.has(sourceId)) {
            copy[key][idx].sourceError = 'Duplicate Task Type'
            valid = false
            return
          }
          seenAdvanceSources.add(sourceId)

          const nameKey = (mapping.name ?? '').trim()
          if (nameKey && seenNameMapping.has(nameKey)) {
            copy[key][idx].nameError = 'Duplicate name'
            valid = false
            return
          }
          if (nameKey) seenNameMapping.add(nameKey)
          hasMappings = true
        } else {
          const missingControl = !mapping.control?.value
          const missingSource = !mapping.source?.value

          const isRowCompletelyEmpty =
            !mapping.control?.value && !mapping.source?.value
          if (isRowCompletelyEmpty) {
            return
          }

          if (missingControl || missingSource) {
            if (missingControl) {
              copy[key][idx].controlError = 'Please select a value'
            }
            if (missingSource) {
              copy[key][idx].sourceError = 'Please select a value'
            }
            valid = false
            return
          }

          // Default duplicate pair check (control+source)
          const controlId = String(mapping.control?.value)
          const sourceId = String(mapping.source?.value)
          if (!seenMappings[sourceId]) {
            seenMappings[sourceId] = new Set()
          }
          if (seenMappings[sourceId].has(controlId)) {
            copy[key][idx].controlError = 'Duplicate entry'
            valid = false
            return
          }
          seenMappings[sourceId].add(controlId)

          hasMappings = true
        }
      })
    })

    if (hasMappings === false) {
      valid = false
      return valid
    }

    setMappings(copy)
    return valid
  }

  return (
    <ReceptorConfigContext.Provider
      value={{
        receptor,
        config,
        setConfig,
        description,
        setDescription,
        mappings,
        addMappingFunc,
        removeMappingFunc,
        errorMsg,
        setMappings,
        updateMappings,
        validateMappings,
        initialMappings,
      }}
    >
      {children}
    </ReceptorConfigContext.Provider>
  )
}
